import styles from "./map.module.css";
import mapboxgl from "mapbox-gl";
import { useState, useRef, useEffect, MouseEvent, useMemo } from "react";
import { drawMapElements, MapDrawingConfig } from "../utils/canvas-drawing";
import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { MapUIOverlay } from "./overlay/MapUIOverlay";
import { MapElement } from "../utils/canvas-drawing";
import { useElementDragging } from "./use-element-dragging";
import {
  mapboxLngLatBoundsFromCoordinateBounds,
  defaultLngLatBounds,
} from "PosterEditor/hooks/use-coordinates";

import {
  cssAspectRatioFromPreset,
  incrementSizeInPX,
} from "PosterEditor/model/poster-project";
import { useEditorUI } from "PosterEditor/use-editor-ui";
import { CoordinateBounds } from "PosterEditor/hooks/use-coordinates";

interface MapProps {
  onMapLoad: (mapReference: any) => void;
  onStaticImageURLFetched: (url: string) => void;
  onClick: () => void;
  onMove: () => void;
  onChangeElementUnderCursor: (element: MapElement | undefined) => void;
  onShowElementEditor: (show: boolean) => void;
  onLoadCanvas: (canvas: any) => void;
}

export const Map = (props: MapProps) => {
  const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ?? "";
  mapboxgl.accessToken = accessToken;
  const {
    project,
    setCoordinateBounds: saveBoundsToProject,
    gpsSource,
    isTemplateEditing,
  } = usePosterProject();
  const {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    pointerLocation,
    setPointerLocation,
    isDragging,
    setDraggingElement,
    elementUnderCursor,
    setElementUnderCursor,
    setSnapLines,
    setContainerSize,
    setMapRef,
  } = useElementDragging();
  const { editorUIState, setMapSizeInPX } = useEditorUI();
  const mapContainer = useRef<any>(null);
  const map = useRef<any>(null);
  const canvasOverlay = useRef<any>(null);

  const listenToMapMoveEvents = () => {
    map.current.on("move", () => {
      props.onMove();
      const bounds = map.current.getBounds();
      const northeast = bounds.getNorthEast();
      const southwest = bounds.getSouthWest();

      const coordinateBounds: CoordinateBounds = {
        northEast: {
          lat: northeast.lat,
          lng: northeast.lng,
        },
        southWest: {
          lat: southwest.lat,
          lng: southwest.lng,
        },
      };
      saveBoundsToProject(coordinateBounds);
    });
  };

  useEffect(() => {
    if (!map.current) {
      return;
    }
    if (editorUIState.editingElement) {
      map.current.keyboard.disable();
    } else {
      map.current.keyboard.enable();
    }
  }, [editorUIState.editingElement]);

  useEffect(() => {
    props.onLoadCanvas(canvasOverlay.current);
  }, [canvasOverlay]);

  useEffect(() => {
    if (map.current) {
      const zoom = map.current.getZoom();
      if (zoom) {
        map.current.easeTo({
          zoom: zoom + 0.4,
        });
      }
    }
  }, [editorUIState.mapZoomInCounter]);

  useEffect(() => {
    if (map.current) {
      const zoom = map.current.getZoom();
      if (zoom) {
        map.current.easeTo({
          zoom: zoom - 0.4,
        });
      }
    }
  }, [editorUIState.mapZoomOutCounter]);

  // CONFIGURE THE MAP.
  useEffect(() => {
    if (!map.current) {
      createMap();
    }
  }, [project.mapStyle, props]);

  useEffect(() => {
    map.current.resize();
  }, [project.borderStyle]);

  const createMap = () => {
    const bounds = project.bounds
      ? mapboxLngLatBoundsFromCoordinateBounds(project.bounds)
      : defaultLngLatBounds;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: project.mapStyle,
      bounds: bounds,
    });
    listenToMapMoveEvents();
    props.onMapLoad(map);

    setMapRef(map);

    if (map.current && project.bounds) {
      const bounds = project.bounds
        ? mapboxLngLatBoundsFromCoordinateBounds(project.bounds)
        : defaultLngLatBounds;
      map.current.fitBounds(bounds, {
        padding: 0, // Optional: padding around the bounds
        duration: 0.3, // Optional: disable animation if needed
      });
    }
  };

  useEffect(() => {
    const bounds = project.bounds
      ? mapboxLngLatBoundsFromCoordinateBounds(project.bounds)
      : defaultLngLatBounds;
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: project.mapStyle,
      bounds: bounds,
    });
    listenToMapMoveEvents();
    props.onMapLoad(map);
  }, [project.mapStyle]);

  // HANDLE MOUSE POINTER INTERUPT
  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!mapContainer.current) return;

      // Calculate the mouse position relative to the mapContainer
      const rect = mapContainer.current.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      setPointerLocation({ x, y });
    };

    const handleMouseLeave = () => {
      setPointerLocation(undefined);
    };

    const container = mapContainer.current;
    if (container) {
      container.addEventListener("mousemove", handleMouseMove);
      container.addEventListener("mouseleave", handleMouseLeave);
    }

    // Cleanup the event listener on component unmount
    return () => {
      if (container) {
        container.removeEventListener("mousemove", handleMouseMove);
        container.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
  }, []);

  // CENTER THE MAP ON LOADING COORDINATES
  useEffect(() => {
    const coords = gpsSource?.allCoordinates;
    if (project.bounds) {
      const bounds = mapboxLngLatBoundsFromCoordinateBounds(project.bounds);
      map.current.fitBounds(bounds, {
        padding: 0,
        duration: 1000,
      });
      requestAnimationFrame(drawContent);
    } else if (coords && coords.length > 1) {
      // Create a bounding box from the coordinates
      const bounds = new mapboxgl.LngLatBounds();
      // Extend the bounds to include each coordinate
      coords.forEach((coord) => {
        bounds.extend([coord.lng, coord.lat]);
      });
      map.current.fitBounds(bounds, { duration: 3000 });
    } else if (coords && coords.length === 1) {
      // If there's only one coordinate, just center the map on it
      map.current.setCenter([coords[0].lng, coords[0].lat]);
      map.current.setZoom(15); // Or whatever zoom level you prefer for a single point
    }
  }, [gpsSource?.allCoordinates, project.styledPoints]);

  const increment = useMemo(
    () => incrementSizeInPX(project.size),
    [project.size]
  );

  const drawContent = () => {
    const drawingConfig: MapDrawingConfig = {
      map,
      canvasElement: canvasOverlay,
      originalMapSize: {
        width: mapContainer.current.offsetWidth,
        height: mapContainer.current.offsetHeight,
      },
      scale: 1,
      project,
      gpsSource,
      dotGridSpacing: isDragging ? increment : undefined,
      pointerLocation,
      pointerInteruptRadius: 50,
      elementToHighlight: undefined,
      selectedElement: editorUIState.editingElement,
      isRendering: false,
      projectEditingPermissions: project.editingPermissions,
      isTemplateEditing: isTemplateEditing,
    };

    const drawResult = drawMapElements(drawingConfig);

    const elementUnderCursor = drawResult.elementUnderPointerLocation;
    setSnapLines({
      vertical: drawResult.snapLines.vertical,
      horizontal: drawResult.snapLines.horizontal,
    });

    const width: number = canvasOverlay.current.width;
    const height: number = canvasOverlay.current.height;
    setMapSizeInPX({ width, height });

    const bounds = map.current.getBounds();
    if (!bounds) {
      return;
    }
    if (map) {
      const type = elementUnderCursor?.type;
      if (
        type === "mapLabel" ||
        type === "staticLabel" ||
        type === "backdrop"
      ) {
        map.current.dragPan.disable();
      } else {
        map.current.dragPan.enable();
      }
    }
    setElementUnderCursor(elementUnderCursor);
    props.onChangeElementUnderCursor(elementUnderCursor);
  };

  // DRAW CONTENT ON MAP.
  useEffect(() => {
    if (map) {
      requestAnimationFrame(drawContent);
      const drawWithAnimationFrame = () => {
        // setEditingElement(undefined);
        requestAnimationFrame(drawContent);
      };

      map.current.on("move", drawWithAnimationFrame);
      map.current.on("zoom", drawWithAnimationFrame);
      // map.current.on("idle", drawWithAnimationFrame);

      return () => {
        map.current.off("move", drawWithAnimationFrame);
        map.current.off("zoom", drawWithAnimationFrame);
        // map.current.on("idle", drawWithAnimationFrame);
      };
    }
  }, [
    map,
    gpsSource?.displayedCoordinates,
    pointerLocation,
    project.lineStyle,
    project.styledPointsUpdateKey,
    project.mapLabels,
    project.labelUpdateKey,
    editorUIState.editingElement,
    project.backdrops,
    project.backdropUpdateKey,
    // editingElement,
  ]);

  useEffect(() => {
    map.current.resize();
    setMapRef(map);
    //requestAnimation  Frame(debouncedUpdateProjectStaticImageOptions);
  }, [project.size]);

  // const drawStaticMapCanvasOverlay = () => {
  //   if (map) {
  //     drawSmoothedLineWithParallelLines({
  //       map,
  //       coordinates: project.gpsSources[0].displayedCoordinates,
  //       targetRef: staticCanvasOverlay,
  //       originalMapWidth: mapContainer.current.offsetWidth,
  //       originalMapHeight: mapContainer.current.offsetHeight,
  //     });
  //   }
  // };
  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!elementUnderCursor) {
      setDraggingElement(undefined);
    } else {
      setDraggingElement(elementUnderCursor);
    }
  };

  return (
    <div
      className={styles.mapContainer}
      onClick={props.onClick}
      style={{
        width: "100%",
        height: "100%",
      }}
    >
      <div
        ref={mapContainer}
        style={{
          width: "100%",
          height: "100%",
          borderRadius: "3px",
          pointerEvents: "auto",
        }}
        onClick={onClick}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      />
      <canvas
        ref={canvasOverlay}
        className={styles.canvasOverlay}
        width={mapContainer.current?.offsetWidth}
        height={mapContainer.current?.offsetHeight}
      />
      <canvas
        // ref={canvasOverlay}
        className={styles.canvasOverlay2}
        width={mapContainer.current?.offsetWidth}
        height={mapContainer.current?.offsetHeight}
      />
      <div
        className={styles.uiOverlay}
        style={{ aspectRatio: cssAspectRatioFromPreset(project.size) }}
      >
        <MapUIOverlay
          pointerLocation={pointerLocation}
          isDragging={isDragging}
          map={map}
          elementUnderCursor={elementUnderCursor}
          onShowElementEditor={(element) => {
            // setEditingElement(element);
          }}
        />
      </div>
    </div>
  );
};
